import { logEvent } from "@src/appV2/lib/analytics";
/* eslint-disable-next-line no-restricted-imports */
import { getFirebaseSingleton } from "@src/lib/firebase/src";

import { getAuthHeaderValue } from "./getAuthorizationHeader";

/**
 * Decodes a JWT token and extracts its payload.
 * @param token - The JWT token string
 * @returns The decoded payload as an object, or undefined if decoding fails
 */
function decodeJwtPayload(token: string): { exp?: number; iat?: number } | undefined {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) {
      return undefined;
    }

    // eslint-disable-next-line prefer-destructuring
    const payload = parts[1];
    // Base64 decode with URL-safe character handling
    // eslint-disable-next-line no-restricted-globals
    const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(decoded) as { exp?: number; iat?: number };
  } catch {
    return undefined;
  }
}

/**
 * Checks if a Firebase ID token is expired.
 * @param token - The Firebase ID token string
 * @returns true if the token is expired or invalid, false otherwise
 */
function isFirebaseTokenExpired(token: string): boolean {
  const payload = decodeJwtPayload(token);

  if (!payload?.exp) {
    // If we can't decode or there's no exp claim, consider it expired
    return true;
  }

  // exp is in seconds since epoch, convert to milliseconds
  const expirationTime = payload.exp * 1000;
  const currentTime = Date.now();

  // Add a small buffer (30 seconds) to account for clock skew and network delays
  const buffer = 30 * 1000;

  return currentTime >= expirationTime - buffer;
}

/**
 * Checks if the current Firebase user's ID token is expired.
 * @returns true if the token is expired, undefined if there's no current user, false otherwise
 */
export async function isCurrentFirebaseTokenExpired(): Promise<boolean | undefined> {
  const firebaseAuth = getFirebaseSingleton();
  const { currentUser } = firebaseAuth;

  if (!currentUser) {
    return undefined;
  }

  try {
    // Get token without forcing refresh to check current expiration
    const token = await currentUser.getIdToken(false);

    if (!token) {
      return true;
    }

    return isFirebaseTokenExpired(token);
  } catch {
    // If getting token fails, consider it expired
    return true;
  }
}

/**
 * Checks if a Firebase token was issued more than a specified number of minutes ago.
 * @param token - The Firebase ID token string
 * @param maxAgeMinutes - Maximum age in minutes (default: 10)
 * @returns true if the token is older than maxAgeMinutes, false otherwise
 */
function isFirebaseTokenTooOld(token: string, maxAgeMinutes = 10): boolean {
  const payload = decodeJwtPayload(token);

  if (!payload?.iat) {
    // If we can't decode or there's no iat claim, consider it too old
    return true;
  }

  // iat is in seconds since epoch, convert to milliseconds
  const issuedAt = payload.iat * 1000;
  const currentTime = Date.now();
  const maxAge = maxAgeMinutes * 60 * 1000; // Convert minutes to milliseconds

  // Check if token is older than maxAge
  return currentTime - issuedAt > maxAge;
}

/**
 * Logs token issue times for debugging purposes.
 * Compares Firebase token vs the token returned by getAuthHeaderValue().
 */
async function logTokenIssueTimesForDebugging(): Promise<void> {
  try {
    const firebaseAuth = getFirebaseSingleton();
    const { currentUser } = firebaseAuth;

    if (!currentUser) {
      return;
    }

    // Get Firebase token directly
    const firebaseToken = await currentUser.getIdToken(false);
    const firebasePayload = firebaseToken ? decodeJwtPayload(firebaseToken) : undefined;
    const firebaseIat = firebasePayload?.iat
      ? new Date(firebasePayload.iat * 1000).toISOString()
      : "unknown";
    const firebaseAgeMinutes = firebasePayload?.iat
      ? (Date.now() - firebasePayload.iat * 1000) / (60 * 1000)
      : undefined;

    // Get token from getAuthHeaderValue (might be Cognito or Firebase depending on FFs)
    const authHeaderValue = await getAuthHeaderValue(false);
    const authToken = authHeaderValue ? authHeaderValue.replace(/^Bearer /, "") : undefined;
    const authPayload = authToken ? decodeJwtPayload(authToken) : undefined;
    const authIat = authPayload?.iat ? new Date(authPayload.iat * 1000).toISOString() : "unknown";
    const authAgeMinutes = authPayload?.iat
      ? (Date.now() - authPayload.iat * 1000) / (60 * 1000)
      : undefined;

    logEvent("TOKEN_AGE_DEBUG_CHECK", {
      firebaseToken: {
        issuedAt: firebaseIat,
        ageMinutes: firebaseAgeMinutes?.toFixed(2),
      },
      authHeaderToken: {
        issuedAt: authIat,
        ageMinutes: authAgeMinutes?.toFixed(2),
        isCognito: authToken && !authToken.includes("firebase"),
      },
    });
  } catch (error) {
    // Log error but don't fail - this is debugging info
    logEvent("TOKEN_AGE_DEBUG_CHECK_FAILED", {
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

/**
 * Checks if the current Firebase user's token is too old (older than 10 minutes).
 * Also logs token issue times for debugging.
 * @returns true if the token is too old, undefined if there's no current user, false otherwise
 */
export async function isCurrentFirebaseTokenTooOld(): Promise<boolean | undefined> {
  const firebaseAuth = getFirebaseSingleton();
  const { currentUser } = firebaseAuth;

  if (!currentUser) {
    return undefined;
  }

  try {
    // Get token without forcing refresh to check current age
    // This gets the cached token that would be used by getIdToken() calls
    const token = await currentUser.getIdToken(false);

    if (!token) {
      return true;
    }

    const isTooOld = isFirebaseTokenTooOld(token);

    // Log token issue times for debugging
    await logTokenIssueTimesForDebugging();

    return isTooOld;
  } catch {
    // If getting token fails, consider it too old
    return true;
  }
}

/**
 * Signs out the current Firebase user if the token is older than 10 minutes.
 * This will trigger Firebase's onIdTokenChanged listener with null, which will
 * handle logout/redirect automatically.
 * @returns true if user was signed out, false if token is recent enough or no user
 */
export async function signOutIfTokenTooOld(): Promise<boolean> {
  const firebaseAuth = getFirebaseSingleton();
  const { currentUser } = firebaseAuth;

  if (!currentUser) {
    return false;
  }

  const isTooOld = await isCurrentFirebaseTokenTooOld();

  if (isTooOld === true) {
    // Sign out the user - this will trigger onIdTokenChanged listener with null
    // which will trigger logout handlers (useIsAuthenticated, useCognitoAuthSync)
    await firebaseAuth.signOut();
    return true;
  }

  return false;
}

/**
 * Ensures the Firebase token is valid by refreshing it if expired.
 * If the token refresh fails (user is no longer authenticated),
 * Firebase's onIdTokenChanged listener will automatically handle logout/redirect.
 *
 * @returns Promise that resolves when token is refreshed (or rejects if refresh fails)
 * @throws Error if there's no current user or if token refresh fails
 */
export async function ensureFirebaseTokenRefreshed(): Promise<void> {
  const firebaseAuth = getFirebaseSingleton();
  const { currentUser } = firebaseAuth;

  if (!currentUser) {
    throw new Error("No Firebase user is authenticated.");
  }

  // Check if token is expired
  const isExpired = await isCurrentFirebaseTokenExpired();

  if (isExpired === true) {
    // Force refresh the token - this will trigger onIdTokenChanged listener
    // If refresh fails, Firebase will automatically call the listener with null
    // which will trigger logout handlers. We let the error propagate so Firebase can handle it.
    await currentUser.getIdToken(true);
  }
}
